/**
 * Persona Service
 * Manages anonymous voter personas and analytics
 */

export interface Persona {
  id: string;
  demographics: {
    ageGroup: string;
    gender: string;
    education: string;
    occupation: string;
    location: string;
    income: string;
  };
  political: {
    partyAffiliation: string;
    politicalLean: string;
    votingFrequency: string;
    issuesPriority: string[];
  };
  media: {
    newsSources: string[];
    socialMediaUsage: string;
    trustLevel: number;
  };
  voting: {
    decisionFactors: string[];
    candidateQualities: string[];
    votingMethod: string;
  };
  createdAt: string;
  lastActive: string;
  votes: {
    candidateId: number;
    candidateName: string;
    timestamp: string;
    electionType: string;
  }[];
}

export interface PersonaAnalytics {
  totalPersonas: number;
  demographics: {
    ageGroups: { [key: string]: number };
    gender: { [key: string]: number };
    education: { [key: string]: number };
    occupation: { [key: string]: number };
    location: { [key: string]: number };
    income: { [key: string]: number };
  };
  political: {
    partyAffiliation: { [key: string]: number };
    politicalLean: { [key: string]: number };
    votingFrequency: { [key: string]: number };
    topIssues: { [key: string]: number };
  };
  media: {
    newsSources: { [key: string]: number };
    socialMediaUsage: { [key: string]: number };
    averageTrustLevel: number;
  };
  voting: {
    decisionFactors: { [key: string]: number };
    candidateQualities: { [key: string]: number };
    votingMethods: { [key: string]: number };
  };
}

class PersonaService {
  private storageKey = 'electure_personas';
  private personas: Persona[] = [];

  constructor() {
    this.loadPersonas();
  }

  private loadPersonas(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.personas = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading personas:', error);
      this.personas = [];
    }
  }

  private savePersonas(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.personas));
    } catch (error) {
      console.error('Error saving personas:', error);
    }
  }

  private generatePersonaId(): string {
    return `persona_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create a new persona
   */
  public createPersona(personaData: Omit<Persona, 'id' | 'createdAt' | 'lastActive' | 'votes'>): Persona {
    const persona: Persona = {
      ...personaData,
      id: this.generatePersonaId(),
      createdAt: new Date().toISOString(),
      lastActive: new Date().toISOString(),
      votes: [],
    };

    this.personas.push(persona);
    this.savePersonas();
    
    return persona;
  }

  public deletePersona(personaId: string): void {
    this.personas = this.personas.filter(p => p.id !== personaId);
    this.savePersonas();
    const currentId = localStorage.getItem('electure_current_persona');
    if (currentId === personaId) localStorage.removeItem('electure_current_persona');
  }

  /**
   * Get current active persona
   */
  public getCurrentPersona(): Persona | null {
    const currentId = localStorage.getItem('electure_current_persona');
    if (currentId) {
      return this.personas.find(p => p.id === currentId) || null;
    }
    return null;
  }

  /**
   * Set current active persona
   */
  public setCurrentPersona(personaId: string): void {
    const persona = this.personas.find(p => p.id === personaId);
    if (persona) {
      localStorage.setItem('electure_current_persona', personaId);
      persona.lastActive = new Date().toISOString();
      this.savePersonas();
    }
  }

  /**
   * Update persona activity
   */
  public updatePersonaActivity(personaId: string): void {
    const persona = this.personas.find(p => p.id === personaId);
    if (persona) {
      persona.lastActive = new Date().toISOString();
      this.savePersonas();
    }
  }

  /**
   * Record a vote for a persona
   */
  public recordVote(personaId: string, candidateId: number, candidateName: string, electionType: string): void {
    const persona = this.personas.find(p => p.id === personaId);
    if (persona) {
      persona.votes.push({
        candidateId,
        candidateName,
        timestamp: new Date().toISOString(),
        electionType,
      });
      persona.lastActive = new Date().toISOString();
      this.savePersonas();
    }
  }

  /**
   * Get all personas
   */
  public getAllPersonas(): Persona[] {
    return this.personas;
  }

  /**
   * Get persona analytics
   */
  public getPersonaAnalytics(): PersonaAnalytics {
    const analytics: PersonaAnalytics = {
      totalPersonas: this.personas.length,
      demographics: {
        ageGroups: {},
        gender: {},
        education: {},
        occupation: {},
        location: {},
        income: {},
      },
      political: {
        partyAffiliation: {},
        politicalLean: {},
        votingFrequency: {},
        topIssues: {},
      },
      media: {
        newsSources: {},
        socialMediaUsage: {},
        averageTrustLevel: 0,
      },
      voting: {
        decisionFactors: {},
        candidateQualities: {},
        votingMethods: {},
      },
    };

    // Process demographics
    this.personas.forEach(persona => {
      // Age groups
      analytics.demographics.ageGroups[persona.demographics.ageGroup] = 
        (analytics.demographics.ageGroups[persona.demographics.ageGroup] || 0) + 1;
      
      // Gender
      analytics.demographics.gender[persona.demographics.gender] = 
        (analytics.demographics.gender[persona.demographics.gender] || 0) + 1;
      
      // Education
      analytics.demographics.education[persona.demographics.education] = 
        (analytics.demographics.education[persona.demographics.education] || 0) + 1;
      
      // Occupation
      analytics.demographics.occupation[persona.demographics.occupation] = 
        (analytics.demographics.occupation[persona.demographics.occupation] || 0) + 1;
      
      // Location
      analytics.demographics.location[persona.demographics.location] = 
        (analytics.demographics.location[persona.demographics.location] || 0) + 1;
      
      // Income
      analytics.demographics.income[persona.demographics.income] = 
        (analytics.demographics.income[persona.demographics.income] || 0) + 1;

      // Political data
      analytics.political.partyAffiliation[persona.political.partyAffiliation] = 
        (analytics.political.partyAffiliation[persona.political.partyAffiliation] || 0) + 1;
      
      analytics.political.politicalLean[persona.political.politicalLean] = 
        (analytics.political.politicalLean[persona.political.politicalLean] || 0) + 1;
      
      analytics.political.votingFrequency[persona.political.votingFrequency] = 
        (analytics.political.votingFrequency[persona.political.votingFrequency] || 0) + 1;

      // Issues priority
      persona.political.issuesPriority.forEach(issue => {
        analytics.political.topIssues[issue] = (analytics.political.topIssues[issue] || 0) + 1;
      });

      // Media data
      persona.media.newsSources.forEach(source => {
        analytics.media.newsSources[source] = (analytics.media.newsSources[source] || 0) + 1;
      });
      
      analytics.media.socialMediaUsage[persona.media.socialMediaUsage] = 
        (analytics.media.socialMediaUsage[persona.media.socialMediaUsage] || 0) + 1;

      // Voting data
      persona.voting.decisionFactors.forEach(factor => {
        analytics.voting.decisionFactors[factor] = (analytics.voting.decisionFactors[factor] || 0) + 1;
      });
      
      persona.voting.candidateQualities.forEach(quality => {
        analytics.voting.candidateQualities[quality] = (analytics.voting.candidateQualities[quality] || 0) + 1;
      });
      
      analytics.voting.votingMethods[persona.voting.votingMethod] = 
        (analytics.voting.votingMethods[persona.voting.votingMethod] || 0) + 1;

      // Trust level
      analytics.media.averageTrustLevel += persona.media.trustLevel;
    });

    // Calculate average trust level
    if (this.personas.length > 0) {
      analytics.media.averageTrustLevel = analytics.media.averageTrustLevel / this.personas.length;
    }

    return analytics;
  }

  /**
   * Get voting patterns by persona type
   */
  public getVotingPatterns(): any {
    const patterns: { [key: string]: any } = {};

    this.personas.forEach(persona => {
      const key = `${persona.demographics.ageGroup}_${persona.political.partyAffiliation}`;
      
      if (!patterns[key]) {
        patterns[key] = {
          count: 0,
          votes: {},
          demographics: persona.demographics,
          political: persona.political,
        };
      }

      patterns[key].count++;
      
      persona.votes.forEach(vote => {
        if (!patterns[key].votes[vote.candidateName]) {
          patterns[key].votes[vote.candidateName] = 0;
        }
        patterns[key].votes[vote.candidateName]++;
      });
    });

    return patterns;
  }

  /**
   * Clear all personas (for testing/reset)
   */
  public clearAllPersonas(): void {
    this.personas = [];
    this.savePersonas();
    localStorage.removeItem('electure_current_persona');
  }

  /**
   * Get persona summary for display
   */
  public getPersonaSummary(persona: Persona): string {
    const age = persona.demographics.ageGroup;
    const location = persona.demographics.location;
    const party = persona.political.partyAffiliation.toUpperCase();
    const issues = persona.political.issuesPriority.slice(0, 2).join(', ');
    
    return `${age} ${location} ${party} voter - Focus: ${issues}`;
  }
}

export default PersonaService;
